import { useEffect, useRef, useState } from "react";
import axiosAdmin from "../../../../api/apiAdmin";
import { BsEnvelope, BsPhone, BsArrowCounterclockwise, BsEye } from "react-icons/bs";

/**
 * One notification, with its email and SMS wording side by side.
 *
 * The SMS length shown is the TRANSLITERATED length, fetched from the server:
 * the gateway receives Latin, and the Georgian original is a completely
 * different number of bytes, so counting in the browser would be wrong in a way
 * that only shows up on the phone bill.
 */
const field =
   "w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 " +
   "dark:text-gray-200 px-3 py-2 text-sm outline-none focus:border-teal-500";
const label = "text-[12px] font-bold text-[#777a83] dark:text-gray-300";

const TemplateCard = ({ template, onSaved, onError }) => {
   const [draft, setDraft] = useState({
      email_subject: template.email_subject || "",
      email_body: template.email_body || "",
      sms_body: template.sms_body || "",
      send_email: !!template.send_email,
      send_sms: !!template.send_sms,
   });
   const [preview, setPreview] = useState(null);
   const [saving, setSaving] = useState(false);
   const [open, setOpen] = useState(false);
   const timer = useRef(null);
   const smsRef = useRef(null);

   const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

   // Debounced: the segment count comes from the server because only it has
   // the transliterator.
   useEffect(() => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
         axiosAdmin
            .post(`/messageTemplates/${template.id}/preview`, {
               email_subject: draft.email_subject,
               email_body: draft.email_body,
               sms_body: draft.sms_body,
            })
            .then(({ data }) => setPreview(data))
            .catch(() => {});
      }, 400);
      return () => clearTimeout(timer.current);
   }, [draft.email_subject, draft.email_body, draft.sms_body, template.id]);

   const save = () => {
      setSaving(true);
      axiosAdmin
         .post(`/messageTemplates/${template.id}`, draft)
         .then(({ data }) => onSaved(data))
         .catch((e) =>
            onError(
               e?.response?.data?.error
                  ? Object.values(e.response.data.error).flat().join(", ")
                  : null
            )
         )
         .finally(() => setSaving(false));
   };

   const reset = () => {
      axiosAdmin
         .post(`/messageTemplates/${template.id}/reset`)
         .then(({ data }) => {
            setDraft({
               email_subject: data.email_subject || "",
               email_body: data.email_body || "",
               sms_body: data.sms_body || "",
               send_email: !!data.send_email,
               send_sms: !!data.send_sms,
            });
            onSaved(data);
         })
         .catch(() => onError());
   };

   const insert = (token) => {
      const el = smsRef.current;
      if (!el) return;
      const at = el.selectionStart ?? draft.sms_body.length;
      set("sms_body", draft.sms_body.slice(0, at) + token + draft.sms_body.slice(at));
      requestAnimationFrame(() => {
         el.focus();
         el.setSelectionRange(at + token.length, at + token.length);
      });
   };

   const segments = preview?.sms_segments ?? template.sms_segments ?? 0;
   const smsLen = preview?.sms_length ?? template.sms_length ?? 0;
   const over = segments > 1;

   return (
      <div className="bg-white dark:bg-slate-700 rounded-xl p-5 flex flex-col gap-3">
         <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
               <p className="font-bold text-[#777a83] dark:text-gray-200">{template.name}</p>
               {template.description && (
                  <p className="text-[11px] text-[#989ba5] mt-0.5">{template.description}</p>
               )}
            </div>
            <div className="flex items-center gap-2">
               <button
                  onClick={() => setOpen((v) => !v)}
                  className="text-xs text-teal-600 font-bold flex items-center gap-1"
               >
                  <BsEye /> {open ? "დამალვა" : "რედაქტირება"}
               </button>
            </div>
         </div>

         {/* Always visible: whether it goes out, and what the SMS costs. */}
         <div className="flex gap-4 flex-wrap items-center text-xs">
            <label className="flex items-center gap-2 cursor-pointer dark:text-slate-200">
               <input
                  type="checkbox"
                  checked={draft.send_email}
                  onChange={(e) => set("send_email", e.target.checked)}
               />
               <BsEnvelope /> მეილი
            </label>
            <label className="flex items-center gap-2 cursor-pointer dark:text-slate-200">
               <input
                  type="checkbox"
                  checked={draft.send_sms}
                  onChange={(e) => set("send_sms", e.target.checked)}
               />
               <BsPhone /> SMS
            </label>
            <span
               className={`px-2 py-1 rounded-full font-bold ${
                  over
                     ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                     : "bg-teal-50 text-teal-700 dark:bg-teal-900/40 dark:text-teal-200"
               }`}
            >
               SMS: {smsLen}/{preview?.segment_size ?? 160} — {segments} შეტყობინება
               {over ? " ⚠ გაიყოფა" : ""}
            </span>
         </div>

         {/* The saved wording, readable without opening the editor — the
             page is a reference as much as a form. Editing shows the draft. */}
         {!open && (
            <div className="grid grid-cols-2 md:grid-cols-1 gap-3 text-sm">
               <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3">
                  <p className="text-[11px] font-bold text-[#989ba5] flex items-center gap-1 mb-1">
                     <BsEnvelope /> მეილი{draft.send_email ? "" : " — გამორთულია"}
                  </p>
                  <p className="font-bold text-slate-800 dark:text-gray-100">{draft.email_subject || "—"}</p>
                  <p className="whitespace-pre-wrap leading-relaxed text-slate-700 dark:text-gray-200 mt-1">
                     {draft.email_body || "—"}
                  </p>
               </div>
               <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3">
                  <p className="text-[11px] font-bold text-[#989ba5] flex items-center gap-1 mb-1">
                     <BsPhone /> SMS{draft.send_sms ? "" : " — გამორთულია"}
                  </p>
                  <p className="whitespace-pre-wrap leading-relaxed text-slate-700 dark:text-gray-200">
                     {draft.sms_body || "—"}
                  </p>
               </div>
            </div>
         )}

         {open && (
            <>
               <div className="flex flex-col gap-1">
                  <span className={label}>მეილის სათაური</span>
                  <input
                     className={field}
                     value={draft.email_subject}
                     onChange={(e) => set("email_subject", e.target.value)}
                  />
               </div>

               <div className="flex flex-col gap-1">
                  <span className={label}>მეილის ტექსტი (ემოჯი შეიძლება)</span>
                  <textarea
                     className={`${field} h-40 font-sans leading-relaxed`}
                     value={draft.email_body}
                     onChange={(e) => set("email_body", e.target.value)}
                  />
               </div>

               <div className="flex flex-col gap-1">
                  <span className={label}>
                     SMS ტექსტი — მოკლედ, ემოჯის გარეშე
                  </span>
                  <textarea
                     ref={smsRef}
                     className={`${field} h-24 ${over ? "border-amber-400" : ""}`}
                     value={draft.sms_body}
                     onChange={(e) => set("sms_body", e.target.value)}
                  />
               </div>

               {/* Placeholders are clickable because typing braces by hand is
                   how you end up with "{amout}" in a customer's message. */}
               {!!template.placeholders?.length && (
                  <div className="flex gap-2 flex-wrap items-center">
                     <span className="text-[11px] text-[#989ba5]">ჩასასმელი მნიშვნელობები:</span>
                     {template.placeholders.map((ph) => (
                        <button
                           key={ph}
                           onClick={() => insert(ph)}
                           className="text-[11px] font-mono bg-slate-100 dark:bg-slate-600
                              dark:text-gray-200 rounded px-2 py-1 hover:bg-teal-100"
                        >
                           {ph}
                        </button>
                     ))}
                  </div>
               )}

               {preview && (
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3 flex flex-col gap-2">
                     <p className="text-[11px] font-bold text-[#989ba5]">ასე გამოიყურება:</p>
                     {draft.send_email && preview.email && (
                        <div>
                           <p className="text-[11px] text-[#989ba5]">მეილი — {preview.subject}</p>
                           <p className="text-sm whitespace-pre-wrap dark:text-gray-200">
                              {preview.email}
                           </p>
                        </div>
                     )}
                     {draft.send_sms && preview.sms && (
                        <div>
                           <p className="text-[11px] text-[#989ba5]">SMS (როგორც გაიგზავნება)</p>
                           <p className="text-sm font-mono break-words dark:text-gray-200">
                              {preview.sms_latin}
                           </p>
                        </div>
                     )}
                  </div>
               )}

               <div className="flex gap-2">
                  <button
                     onClick={save}
                     disabled={saving}
                     className="rounded-lg bg-teal-500 disabled:opacity-50 text-white font-bold
                        text-sm px-5 py-2"
                  >
                     შენახვა
                  </button>
                  <button
                     onClick={reset}
                     className="rounded-lg bg-slate-400 text-white font-bold text-sm px-4 py-2
                        flex items-center gap-2"
                  >
                     <BsArrowCounterclockwise /> საწყისზე დაბრუნება
                  </button>
               </div>
            </>
         )}
      </div>
   );
};

export default TemplateCard;
